import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_MS = 150

export default class extends Controller {
  static targets = [
    "addButton",
    "picker",
    "input",
    "results",
    "form",
    "related"
  ]
  static values = { searchUrl: String }

  disconnect() {
    clearTimeout(this.searchTimeout)
    clearTimeout(this.closeTimeout)
    this.abortController?.abort()
  }

  open() {
    this.addButtonTarget.classList.add("hidden")
    this.pickerTarget.classList.remove("hidden")
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.search()
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    clearTimeout(this.searchTimeout)
    this.abortController?.abort()
    this.pickerTarget.classList.add("hidden")
    this.resultsTarget.classList.add("hidden")
    this.addButtonTarget.classList.remove("hidden")
  }

  closeSoon() {
    this.closeTimeout = setTimeout(() => this.close(), 150)
  }

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.fetchResults(), DEBOUNCE_MS)
  }

  async fetchResults() {
    this.abortController?.abort()
    this.abortController = new AbortController()

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", this.inputTarget.value)

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })
      if (!response.ok) return

      this.renderResults(await response.json())
    } catch (error) {
      if (error.name !== "AbortError")
        console.error("Related search failed:", error)
    }
  }

  renderResults(tasks) {
    this.resultsTarget.replaceChildren()
    this.activeIndex = tasks.length ? 0 : -1

    if (!tasks.length) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-1.5 text-sm text-fg-muted"
      empty.textContent = "No matching tasks"
      this.resultsTarget.append(empty)
    }

    tasks.forEach((task) => {
      const option = document.createElement("button")
      option.type = "button"
      option.role = "option"
      option.dataset.identifier = task.identifier
      option.className =
        "w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-primary/10"

      const identifier = document.createElement("span")
      identifier.className = "text-xs text-fg-muted flex-shrink-0 thick"
      identifier.textContent = task.identifier

      const title = document.createElement("span")
      title.className = "truncate font-light"
      title.textContent = task.title || "Untitled"

      option.append(identifier, title)
      option.addEventListener("mousedown", (event) => event.preventDefault())
      option.addEventListener("click", () => this.add(task.identifier))
      this.resultsTarget.append(option)
    })

    this.resultsTarget.classList.remove("hidden")
    this.highlight()
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll("[role=option]"))
  }

  next(event) {
    event.preventDefault()
    if (!this.options.length) return
    this.activeIndex = (this.activeIndex + 1) % this.options.length
    this.highlight()
  }

  previous(event) {
    event.preventDefault()
    if (!this.options.length) return
    this.activeIndex =
      (this.activeIndex - 1 + this.options.length) % this.options.length
    this.highlight()
  }

  highlight() {
    this.options.forEach((option, index) => {
      const active = index === this.activeIndex
      option.classList.toggle("bg-primary/10", active)
      option.setAttribute("aria-selected", active)
      if (active) option.scrollIntoView({ block: "nearest" })
    })
  }

  choose(event) {
    event.preventDefault()
    const option = this.options[this.activeIndex]
    if (option) this.add(option.dataset.identifier)
  }

  add(identifier) {
    clearTimeout(this.closeTimeout)
    this.relatedTarget.value = identifier
    this.formTarget.requestSubmit()
    this.close()
  }
}
